import React, {useEffect, useRef} from 'react'
import clsx from 'clsx'
import {useLocation} from 'react-router'
import {checkIsActive, KTSVG} from '../../../helpers'
import {useLayout} from '../../core'

type Props = {
  to: string
  title: string
  icon?: string
  fontIcon?: string
  hasBullet?: boolean
  // Set by a menu that knows where the current page is: `active` when one of the sub-menus
  // is the current page, `open` to show the sub-menus. Left out, the URL decides both.
  active?: boolean
  open?: boolean
}

const AsideMenuItemWithSub: React.FC<Props> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet,
  active,
  open,
}) => {
  const {pathname} = useLocation()
  const isActive = active ?? checkIsActive(pathname, to)
  const isOpen = open ?? isActive
  const {config} = useLayout()
  const {aside} = config
  const itemRef = useRef<HTMLDivElement | null>(null)
  const subRef = useRef<HTMLDivElement | null>(null)

  // The accordion script (MenuComponent) closes a menu by removing `show` and leaving an inline
  // `display: none` on its sub-menus, neither of which React sees. Undo both when this menu
  // opens, and on every page change inside it, so the current page's menu is never left shut.
  const pageInside = isActive ? pathname : ''
  useEffect(() => {
    if (isOpen && itemRef.current && subRef.current) {
      itemRef.current.classList.add('show')
      subRef.current.style.display = ''
      subRef.current.style.overflow = ''
    }
  }, [isOpen, pageInside])

  return (
    <div
      ref={itemRef}
      className={clsx('menu-item', {here: isActive, show: isOpen}, 'menu-accordion')}
      data-kt-menu-trigger='click'
    >
      <span className='menu-link'>
        {hasBullet && (
          <span className='menu-bullet'>
            <span className='bullet bullet-dot'></span>
          </span>
        )}
        {icon && aside.menuIcon === 'svg' && (
          <span className='menu-icon'>
            <KTSVG path={icon} className='svg-icon-2' />
          </span>
        )}
        {fontIcon && aside.menuIcon === 'font' && <i className={clsx('bi fs-3', fontIcon)}></i>}
        <span className='menu-title'>{title}</span>
        <span className='menu-arrow'></span>
      </span>
      <div
        ref={subRef}
        className={clsx('menu-sub menu-sub-accordion', {'menu-active-bg': isActive})}
      >
        {children}
      </div>
    </div>
  )
}

export {AsideMenuItemWithSub}
